// ./src/components/features/PrintableSubmission/PrintableSubmission.tsx
import React, { useMemo } from "react";
import { Box, Text, Stack, Group, Divider } from "@mantine/core";
import dayjs from "dayjs";

import { Submission, SubmissionFile } from "@/utils/api";
import { Form, FormField } from "@/utils/api";
import { Logo } from "@/components/generic/Logo";
import { PrintableField } from "./PrintableField";
import { PrintableFileList } from "./PrintableFileList";
import { FIELD_TYPE_IDS } from "@/config/fieldTypes";

export interface PrintableSubmissionProps {
  /** Submission data */
  submission: Submission;
  /** Form configuration */
  form: Form;
  /** Form field definitions (sorted by orderNo) */
  formFields: FormField[];
  /** Whether to show metadata section (IP, user agent, referrer) */
  showMetadata?: boolean;
  /** Whether to show full credit card numbers (default: masked) */
  showFullCreditCard?: boolean;
  /** Whether to show FormSubmits logo (false for Business plan) */
  showLogo?: boolean;
  /** Additional class name for styling */
  className?: string;
}

/**
 * Parses formData JSON string into an object
 */
const parseFormData = (formData: string | undefined): Record<string, any> => {
  if (!formData) return {};

  try {
    return JSON.parse(formData);
  } catch {
    return {};
  }
};

/**
 * Formats submission date/time to local timezone
 */
const formatSubmissionDate = (isoDate: string | undefined): string => {
  if (!isoDate) return "Unknown date";

  const date = dayjs(isoDate);
  if (!date.isValid()) return "Unknown date";

  return date.format("MMMM D, YYYY [at] h:mm A");
};

/**
 * Truncates user agent string for display
 */
const formatUserAgent = (userAgent: string | undefined): string => {
  if (!userAgent) return "Unknown";

  // If it's very long, truncate it
  if (userAgent.length > 150) {
    return userAgent.substring(0, 150) + "...";
  }

  return userAgent;
};

/**
 * Filters out non-printable fields (files, anti-spam)
 */
const getPrintableFields = (formFields: FormField[]): FormField[] => {
  const skipTypes = [
    FIELD_TYPE_IDS.FILE,
    FIELD_TYPE_IDS.ANTISPAMBOT,
    FIELD_TYPE_IDS.RECAPTCHA2,
    FIELD_TYPE_IDS.RECAPTCHA3,
    FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE,
  ];

  return formFields.filter(
    (field) =>
      field.fieldType === undefined || !skipTypes.includes(field.fieldType)
  );
};

export const PrintableSubmission: React.FC<PrintableSubmissionProps> = ({
  submission,
  form,
  formFields,
  showMetadata = false,
  showFullCreditCard = false,
  showLogo = true,
  className,
}) => {
  // Parse form data
  const formData = useMemo(
    () => parseFormData(submission.formData),
    [submission.formData]
  );

  // Get printable fields (exclude files, recaptcha, etc.)
  const printableFields = useMemo(
    () => getPrintableFields(formFields),
    [formFields]
  );

  // Get submission files
  const files: SubmissionFile[] = submission.submissionFiles || [];

  return (
    <Box className={className}>
      {/* Document Header */}
      <Box className="print-section" mb="lg">
        {/* Logo (conditional) */}
        {showLogo && (
          <Box mb="md">
            <Logo size="small" />
          </Box>
        )}

        {/* Form name and submission info */}
        <Stack gap="xs">
          <Text size="xl" fw={700}>
            {form.name || "Form Submission"}
          </Text>

          {form.description && (
            <Text size="sm" c="dimmed">
              {form.description}
            </Text>
          )}

          <Group gap="lg" mt="xs">
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase">
                Submission Date
              </Text>
              <Text size="sm" fw={500}>
                {formatSubmissionDate(submission.submissionDttm)}
              </Text>
            </Box>

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase">
                Submission ID
              </Text>
              <Text size="sm" fw={500} style={{ fontFamily: "monospace" }}>
                {submission.id || "—"}
              </Text>
            </Box>
          </Group>
        </Stack>
      </Box>

      <Divider mb="lg" />

      {/* Metadata Section (optional) */}
      {showMetadata && (
        <>
          <Box className="print-section" mb="lg">
            <Text
              size="sm"
              fw={700}
              mb="sm"
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                borderBottom: "2px solid var(--mantine-color-gray-4)",
                paddingBottom: "8px",
              }}
            >
              Submission Metadata
            </Text>

            <Stack gap="xs">
              {/* IP Address */}
              <Box
                className="print-field-group"
                py="xs"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-2)",
                }}
              >
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  IP Address
                </Text>
                <Text size="sm" style={{ fontFamily: "monospace" }}>
                  {submission.remoteIp || "Unknown"}
                </Text>
              </Box>

              {/* User Agent */}
              <Box
                className="print-field-group"
                py="xs"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-2)",
                }}
              >
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  User Agent
                </Text>
                <Text
                  size="sm"
                  style={{
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                  }}
                >
                  {formatUserAgent(submission.userAgent)}
                </Text>
              </Box>

              {/* Referrer */}
              <Box
                className="print-field-group"
                py="xs"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-2)",
                }}
              >
                <Text
                  size="sm"
                  fw={600}
                  c="dimmed"
                  style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Referrer
                </Text>
                <Text size="sm" style={{ wordBreak: "break-word" }}>
                  {submission.referrer || "Direct / No referrer"}
                </Text>
              </Box>
            </Stack>
          </Box>

          <Divider mb="lg" />
        </>
      )}

      {/* Form Fields Section */}
      <Box className="print-section" mb="lg">
        <Text
          size="sm"
          fw={700}
          mb="sm"
          style={{
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            borderBottom: "2px solid var(--mantine-color-gray-4)",
            paddingBottom: "8px",
          }}
        >
          Submitted Data
        </Text>

        <Stack gap={0}>
          {printableFields.length > 0 ? (
            printableFields.map((field) => (
              <PrintableField
                key={field.id || field.name}
                field={field}
                value={formData[field.name || ""]}
                showFullCreditCard={showFullCreditCard}
              />
            ))
          ) : (
            <Text size="sm" c="dimmed" fs="italic" py="md">
              No form fields configured
            </Text>
          )}
        </Stack>
      </Box>

      {/* Files Section */}
      {files.length > 0 && (
        <>
          <Divider mb="lg" />
          <PrintableFileList files={files} />
        </>
      )}

      {/* Footer */}
      <Box
        mt="xl"
        pt="md"
        style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
      >
        <Text size="xs" c="dimmed" ta="center">
          Generated on {dayjs().format("MMMM D, YYYY [at] h:mm A")}
          {showLogo && " • FormSubmits.com"}
        </Text>
      </Box>
    </Box>
  );
};
